import React, { useCallback, useState } from 'react';
import { Modal } from '../../design-system';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ProgressBar } from './progress-bar/progress-bar.component';
import { FailUpload, DuplicatedFile, UploadFile } from './upload-file-popup';
import { useSelector } from 'src/store/use-selector';
import {
  MOV_FILE_EXTENSION,
  SMART_FORMAT_COMPATIBLE_EXTENSIONS,
} from 'shared/constants/assets.constants';
import { useUploadLogic } from './hooks/upload.hooks';
import { useFileValidation } from './hooks/validation.hooks';
import { UploadFileBinderComponent } from './upload-file-binder/upload-file-binder.component';
import { FileRejection } from 'react-dropzone';
import { generateHash } from 'shared/utils';

type EditContentFn = (arg: {
  id: string;
  categoryId: string;
  ownerIds: string[];
  title: string;
  content: unknown;
  link: string;
  type: string;
  fileName: string;
}) => void;

function KnowledgeUploadFileComponent({
  open = false,
  close = () => {},
  editContent = () => {},
  editExternal = ({ id, categoryId, ownerIds, title, link, type, fileName }) => {},
  notificationApi,
  convertPastItemToSmartFormat = false,
  bypassOpen = false,
  ...props
}: {
  id: string;
  title: string;
  open?: boolean;
  close: () => void;
  editContent: EditContentFn;
  bypassOpen: boolean;
  editExternal: (arg: {
    id: string;
    categoryId: string;
    ownerIds: string[];
    title: string;
    link: string;
    type: string;
    fileName: string;
  }) => void;
  notificationApi: {
    success: (args: { message: string; placement: string }) => void;
    error: (args: { message: string; placement: string }) => void;
  };
  convertPastItemToSmartFormat: boolean;
  categoryId: string;
  ownerIds: string[];
}) {
  const lang = useSelector(getLang('KNOWLEDGE_UPLOAD_POPUP'));
  const [bulkUploadFiles, setBulkUploadFiles] = useState(
    [] as {
      name: string;
      error?: string;
    }[],
  );
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const isBulkUpload = bulkUploadFiles.length > 0;

  const {
    loading,
    failed,
    failureReason,
    duplication,
    file,
    asset,
    progress,
    status,
    isSmartFormat,
    toggleSmartFormat,
    uploadFile,
    convertToSmartFormat,
    cancelOnDuplication,
    cancelProgress,
    retryUpload,
    uploadFileHandler,
    reset,
  } = useUploadLogic({
    convertPastItemToSmartFormat,
    editExternal,
    close,
    props,
    isCancelDialogOpen,
  });

  const handleAfterOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        reset();
        setIsCancelDialogOpen(false);
      }
    },
    [reset],
  );

  const handleBulkUploadClose = useCallback(() => {
    setBulkUploadFiles([]);
    close();
  }, [close]);

  const onSubmit = async () => {
    if (isSmartFormat) {
      convertToSmartFormat();
    } else {
      uploadFile();
    }
  };

  const checkUploadDuplicates = async (files: File[]) => {
    const hashSet = new Set<string>();
    const duplicates: { name: string; error: string }[] = [];
    const uniqueFiles: File[] = [];

    for (const file of files) {
      const hash = await generateHash(file);

      if (hashSet.has(hash)) {
        duplicates.push({ name: file.name, error: 'duplicated-upload' });
      } else {
        hashSet.add(hash);
        uniqueFiles.push(file);
      }
    }

    return { uniqueFiles, duplicates };
  };

  const handleBulkUpload = async (files: File[], fileRejections: FileRejection[]) => {
    const filesWithErrors = fileRejections.map(({ file, errors }) => {
      const [error] = errors.map(error => error.code);

      return {
        name: file.name,
        error,
      };
    });

    const { uniqueFiles, duplicates: uploadDuplicates } = await checkUploadDuplicates(files);

    const filesWithoutErrors = uniqueFiles.filter(
      f => !filesWithErrors.find(el => el.name === f.name),
    );

    setBulkUploadFiles([...filesWithoutErrors, ...filesWithErrors, ...uploadDuplicates]);
  };

  const { fileValidationError, fileExtension } = useFileValidation({ file, isSmartFormat });
  const isMov = fileExtension === MOV_FILE_EXTENSION;
  const isSmartFormatAvailable = SMART_FORMAT_COMPATIBLE_EXTENSIONS.includes(fileExtension || '');

  const getModalTitle = () => {
    if (duplication) return lang.DUPLICATION_TITLE;
    if (failed || fileValidationError) return lang.FAIL_TITLE;
    return lang.TITLE;
  };

  return (
    <>
      <Modal
        open={open && !isBulkUpload && !bypassOpen}
        onCancel={close}
        afterOpenChange={handleAfterOpenChange}
        footer={null}
        title={getModalTitle()}>
        {(() => {
          // On duplicate recognized - show the duplicate knowledge item and allow to link or to continue
          if (duplication) {
            return (
              <DuplicatedFile
                duplicatedItemId={duplication?.knowledgeId}
                assetId={asset?.assetId}
                onCancel={cancelOnDuplication}
                onClose={close}
                notificationApi={notificationApi}
                props={props}
              />
            );
          }

          const error = failed ? (failureReason ?? 'upload-failed') : fileValidationError;

          if (error) {
            return (
              <FailUpload
                error={error as any}
                onCancel={close}
                onRetry={retryUpload}
                disableRetry={convertPastItemToSmartFormat}
              />
            );
          }

          // Loading phase
          if (loading) {
            return (
              <ProgressBar
                fileName={file?.name}
                status={status}
                progress={progress}
                onCancel={cancelProgress}
                isDirectConversion={convertPastItemToSmartFormat}
                onCancelDialogChange={(open: boolean) => setIsCancelDialogOpen(open)}
              />
            );
          }

          /*
          This is so you don't get the FileUpload input flash before the useEffect has run,
          when you are just converting a previously uploaded file.
        */
          if (convertPastItemToSmartFormat) {
            return <></>;
          }

          // Initial phase
          return (
            <UploadFile
              isMov={isMov}
              isSmartFormatAvailable={isSmartFormatAvailable}
              fileExtension={fileExtension}
              smartFormat={isSmartFormat}
              toggleSmartFormat={toggleSmartFormat}
              onUploadFile={uploadFileHandler}
              onSubmit={onSubmit}
              onCancel={close}
              disabled={!!(!file || fileValidationError)}
              handleBulkUpload={handleBulkUpload}
            />
          );
        })()}
      </Modal>
      {isBulkUpload && (
        <UploadFileBinderComponent
          files={bulkUploadFiles}
          onClose={handleBulkUploadClose}
          props={props}
        />
      )}
    </>
  );
}

export { KnowledgeUploadFileComponent };
